// ============================================================================
// COURT PLACE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { courtPlaceService } from '../../api/services/courtPlaceService';
import type {
  CourtPlace,
  CourtPlaceListItem,
  CreateCourtPlaceRequest,
  UpdateCourtPlaceRequest,
  CourtPlaceSearchRequest,
} from '../../api/types/courtPlace.types';
import type { ID } from '../../api/types';

export const useCourtPlaces = (params?: CourtPlaceSearchRequest) => {
  const [data, setData] = useState<CourtPlaceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await courtPlaceService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useCourtPlace = (id: ID | undefined) => {
  const [data, setData] = useState<CourtPlace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await courtPlaceService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateCourtPlace = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createItem = async (
    data: CreateCourtPlaceRequest,
    options?: { onSuccess?: (item: CourtPlace) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await courtPlaceService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createCourtPlace: createItem, loading, error };
};

export const useUpdateCourtPlace = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateItem = async (
    id: ID, data: UpdateCourtPlaceRequest,
    options?: { onSuccess?: (item: CourtPlace) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await courtPlaceService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateCourtPlace: updateItem, loading, error };
};

export const useDeleteCourtPlace = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteItem = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await courtPlaceService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteCourtPlace: deleteItem, loading, error };
};
